import React, { useEffect, useState, createElement } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { DollarSignIcon, AlertTriangleIcon } from 'lucide-react';
import { calculateValuation, QuizData, ValuationResult } from '../utils/ValuationCalculator';
import { getQuizData } from '../utils/quizDataPersistence';
// Mock data for preview mode
const PREVIEW_DATA: QuizData = {
  industry: 'chiropractic',
  industryOther: '',
  isOwner: 'yes',
  state: 'CA',
  yearsInBusiness: '3',
  revenue: '0-1M',
  profit: '15-25',
  locations: '1',
  providers: '1',
  payerMix: '50-75-insurance',
  concentration: 'no',
  trend: 'up-5-10',
  timeline: '12-24m',
  ownerHours: '40+'
};
export function NonQualifiedResultsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [result, setResult] = useState<ValuationResult | null>(null);
  // Try to get form data from navigation state first, then fall back to localStorage, then preview data
  const formData = location.state as QuizData & {
    firstName: string;
    email: string;
  } || getQuizData() as any || {
    ...PREVIEW_DATA,
    firstName: 'Preview',
    email: 'preview@example.com'
  };
  useEffect(() => {
    console.log('NonQualifiedResultsViewed', formData);
    // Calculate valuation from quiz data
    const calculatedResult = calculateValuation(formData as QuizData);
    setResult(calculatedResult);
  }, [formData]);
  useEffect(() => {
    // Load Wistia scripts
    const script1 = document.createElement('script');
    script1.src = 'https://fast.wistia.com/player.js';
    script1.async = true;
    document.body.appendChild(script1);
    const script2 = document.createElement('script');
    script2.src = 'https://fast.wistia.com/embed/me9xxd1792.js';
    script2.async = true;
    script2.type = 'module';
    document.body.appendChild(script2);
    return () => {
      document.body.removeChild(script1);
      document.body.removeChild(script2);
    };
  }, []);
  if (!result) {
    return <div className="min-h-screen bg-white flex items-center justify-center" data-id="element-155">
        <div className="text-center" data-id="element-156">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4" data-id="element-157"></div>
          <p className="text-gray-600" data-id="element-158">Calculating your valuation...</p>
        </div>
      </div>;
  }
  const formatCurrency = (value: number) => {
    const roundedValue = Math.round(value / 100000) * 100000;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(roundedValue);
  };
  const getKeyManRiskExplanation = () => {
    if (result.keyManRisk === 'High') {
      return 'Because you handle most patient visits, your value is reduced to reflect owner dependency. Training associates and reducing your clinical hours could add significant value.';
    } else if (result.keyManRisk === 'Moderate') {
      return 'Your clinic has some owner dependency. Delegating more patient care to associates could increase your multiple.';
    } else {
      return 'Your clinic operates well without heavy owner involvement, which increases its value to buyers.';
    }
  };
  return <div className="min-h-screen bg-white py-6 md:py-12 px-4" data-id="element-159">
      {/* Logo */}
      <div className="max-w-4xl mx-auto mb-6" data-id="element-160">
        <div className="animate-fade-in" data-id="element-161">
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/qmxPrQcAoJQeBtBGYhM9D6/516lion.png" alt="516 Partners Logo" className="h-16 md:h-24 mx-auto" data-id="element-162" />
        </div>
      </div>

      <div className="max-w-4xl mx-auto space-y-8 md:space-y-12" data-id="element-163">
        {/* Results Header */}
        <div className="bg-white border border-gray-200 p-4 sm:p-6 md:p-8 lg:p-12 animate-fade-in-scale" data-id="element-164">
          <div className="text-center mb-4 animate-fade-in" data-id="element-165">
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-serif font-bold text-gray-900 mb-2 leading-tight" data-id="element-166">
              Here's Your Estimated Clinic Valuation
            </h1>
            <p className="text-base sm:text-lg font-bold text-gray-600" data-id="element-167">
              Step 1: Watch the video to understand your results
            </p>
          </div>

          {/* Wistia Video */}
          <div className="mb-8 md:mb-12 animate-stagger-in animate-delay-100" data-id="element-168">
            <style dangerouslySetInnerHTML={{
            __html: `
                  wistia-player[media-id='me9xxd1792']:not(:defined) { 
                    background: center / contain no-repeat url('https://fast.wistia.com/embed/medias/me9xxd1792/swatch'); 
                    display: block; 
                    filter: blur(5px); 
                    padding-top: 56.25%; 
                  }
                `
          }} data-id="element-169" />
            <wistia-player media-id="me9xxd1792" aspect="1.7777777777777777" style={{
            width: '100%',
            display: 'block'
          }} data-id="element-170" />
          </div>

          {/* Non-Qualified Message */}
          <div className="mb-8 md:mb-12 animate-stagger-in animate-delay-200" data-id="element-171">
            <div className="bg-white border border-gray-200 p-6 sm:p-8 text-center" data-id="element-172">
              <h2 className="text-xl sm:text-2xl font-serif font-bold text-gray-900 mb-4" data-id="element-173">
                Want to Discuss Your Results?
              </h2>
              <p className="text-base sm:text-lg text-gray-600 mb-6 max-w-2xl mx-auto" data-id="element-174">
                Our review calls are reserved for clinics doing $1M+ in annual
                revenue. If you're approaching that threshold or have questions
                about growing to that level, reach out to us directly.
              </p>
              <div className="space-y-4" data-id="element-175">
                <p className="text-gray-900 font-medium text-base sm:text-lg" data-id="element-176">
                  Email us at{' '}
                  <a href="mailto:ivan@516partners.com" className="underline hover:text-yellow-600 transition-colors" data-id="element-177">
                    ivan@516partners.com
                  </a>
                </p>
                <p className="text-sm text-gray-500" data-id="element-178">
                  We're here to help you understand your valuation and plan your
                  path forward.
                </p>
              </div>
            </div>
          </div>

          {/* Valuation Range */}
          <div className="bg-gray-50 border-2 border-gray-900 p-6 sm:p-8 mb-6 md:mb-8 animate-stagger-in animate-delay-300" data-id="element-179">
            <p className="text-xs sm:text-sm font-medium text-gray-600 uppercase tracking-wide mb-2 text-center" data-id="element-180">
              Your Clinic's Range
            </p>
            <div className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-2 text-center whitespace-nowrap" data-id="element-181">
              {formatCurrency(result.low)} – {formatCurrency(result.high)}
            </div>
            <p className="text-xs sm:text-sm text-gray-600 text-center" data-id="element-182">
              Based on your answers. Not an appraisal.
            </p>
          </div>

          {/* Key Man Risk Alert */}
          {result.keyManRisk === 'High' && <div className="mb-6 md:mb-8 p-4 sm:p-6 bg-yellow-50 border border-yellow-600 animate-stagger-in animate-delay-400" data-id="element-183">
              <div className="flex items-start space-x-3" data-id="element-184">
                <AlertTriangleIcon className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-600 flex-shrink-0 mt-1" data-id="element-185" />
                <div data-id="element-186">
                  <h3 className="font-semibold text-gray-900 mb-2 text-sm sm:text-base" data-id="element-187">
                    Owner Dependency Impact
                  </h3>
                  <p className="text-gray-700 text-sm sm:text-base" data-id="element-188">
                    {getKeyManRiskExplanation()}
                  </p>
                </div>
              </div>
            </div>}

          {/* How We Calculated Your Range */}
          <div className="mb-6 md:mb-8 animate-stagger-in animate-delay-500" data-id="element-189">
            <h2 className="text-xl sm:text-2xl font-serif font-bold text-gray-900 mb-2 text-center" data-id="element-190">
              How It Was Calculated...
            </h2>
            <p className="text-sm text-gray-600 text-center mb-6" data-id="element-191">
              These are extremely rough estimates, not exact numbers.
            </p>

            <div className="max-w-2xl mx-auto space-y-4 sm:space-y-6" data-id="element-192">
              {/* EBITDA */}
              <div className="flex items-center justify-between pb-4 border-b border-gray-200 gap-4 animate-stagger-in animate-delay-100" data-id="element-193">
                <div className="flex items-center space-x-2 sm:space-x-3 min-w-0" data-id="element-194">
                  <DollarSignIcon className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 flex-shrink-0" data-id="element-195" />
                  <span className="text-gray-900 font-medium text-sm sm:text-base truncate" data-id="element-196">
                    Annual Profit (EBITDA)
                  </span>
                </div>
                <span className="text-gray-900 font-bold text-sm sm:text-base whitespace-nowrap" data-id="element-197">
                  {formatCurrency(result.ebitda)}
                </span>
              </div>

              {/* Multiple */}
              <div className="flex items-center justify-between pb-4 border-b border-gray-200 gap-4 animate-stagger-in animate-delay-200" data-id="element-198">
                <span className="text-gray-900 font-medium text-sm sm:text-base min-w-0 truncate" data-id="element-199">
                  Market Multiple Applied
                </span>
                <span className="text-gray-900 font-bold text-sm sm:text-base whitespace-nowrap" data-id="element-200">
                  {Math.round(result.multipleRange[0])}x–
                  {Math.round(result.multipleRange[1])}x
                </span>
              </div>
            </div>
          </div>

          {/* Value Unlocks */}
          <div className="mb-6 md:mb-8 animate-stagger-in animate-delay-600" data-id="element-201">
            <h2 className="text-xl sm:text-2xl font-serif font-bold text-gray-900 mb-4" data-id="element-202">
              Quick wins we see next:
            </h2>
            <div className="space-y-3" data-id="element-203">
              <div className="flex items-start space-x-3 p-3 sm:p-4 border border-gray-300 animate-stagger-in animate-delay-100" data-id="element-204">
                <span className="text-gray-900 font-bold flex-shrink-0" data-id="element-205">•</span>
                <p className="text-gray-900 text-sm sm:text-base" data-id="element-206">
                  Find hidden expenses that can increase your value
                </p>
              </div>
              <div className="flex items-start space-x-3 p-3 sm:p-4 border border-gray-300 animate-stagger-in animate-delay-200" data-id="element-207">
                <span className="text-gray-900 font-bold flex-shrink-0" data-id="element-208">•</span>
                <p className="text-gray-900 text-sm sm:text-base" data-id="element-209">
                  Reduce dependence on any single insurance or referral source
                </p>
              </div>
              <div className="flex items-start space-x-3 p-3 sm:p-4 border border-gray-300 animate-stagger-in animate-delay-300" data-id="element-210">
                <span className="text-gray-900 font-bold flex-shrink-0" data-id="element-211">•</span>
                <p className="text-gray-900 text-sm sm:text-base" data-id="element-212">
                  Improve profit margins in ways buyers care about
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}